// Grouped roster (Brahmotsavam vahana): many sponsors per vahanam, two roles, matched to legacy donations.
// Vahanams are a small bounded set and sponsors grow over time, so the roster is organised as a summary of
// collapsible vahanam sections (accordion). Each sponsor row carries its matched pledge / status / balance.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Alert, Button, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, FormControl, InputLabel, Select, MenuItem,
  TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails, Autocomplete, Link as MuiLink,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import { Link as RouterLink } from 'react-router-dom';
import PageLayout from '../../components/common/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { get, patch } from '../../utils/api';
import {
  fmtDate, fmtDateLong, escHtml, roleChip, roleText, printHtml,
  NoteDialog, MapDonorDialog, PledgeHistoryDialog, EmailDialog, AddSponsorDialog, RecognitionDialog, RosterHeader, LinkedEventBanner,
} from './shared';

// One collapsible vahanam section: header with counts + schedule editor + its sponsor rows.
function VahanaSection({ item, donors, noun, schedNoun, open, onToggle, onMap, onRecognition, onPledge, onNote, onEmail, onSchedSaved }) {
  const [schedDate, setSchedDate] = useState(item.procession_date ? String(item.procession_date).slice(0, 10) : '');
  const [schedTime, setSchedTime] = useState(item.procession_time || '');
  const [savingSched, setSavingSched] = useState(false);
  useEffect(() => {
    setSchedDate(item.procession_date ? String(item.procession_date).slice(0, 10) : '');
    setSchedTime(item.procession_time || '');
  }, [item.procession_date, item.procession_time]);
  const daata = donors.filter((d) => d.role === 'daata').length;
  const ubhaya = donors.filter((d) => d.role === 'ubhaya_daata').length;
  const pledged = donors.filter((d) => d.source === 'pledge').length;
  const saveSched = async () => {
    setSavingSched(true);
    try { await patch(`/api/manage/special-sponsorships/items/${item.id}`, { procession_date: schedDate || null, procession_time: schedTime || null }); await onSchedSaved(); }
    finally { setSavingSched(false); }
  };
  return (
    <Accordion expanded={open} onChange={onToggle} disableGutters sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, boxShadow: 'none' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%', pr: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{item.name}</Typography>
          {item.procession_date && <Typography variant="caption" color="text.secondary">{fmtDateLong(item.procession_date)}{item.procession_time ? ` · ${item.procession_time}` : ''}</Typography>}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${donors.length} sponsor${donors.length === 1 ? '' : 's'}`} />
            {daata > 0 && <Chip size="small" variant="outlined" color="primary" label={`${daata} ${noun} Daata`} />}
            {ubhaya > 0 && <Chip size="small" variant="outlined" color="warning" label={`${ubhaya} Ubhaya`} />}
            {pledged > 0 && <Chip size="small" variant="outlined" color="success" label={`${pledged} pledged`} />}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
          <TextField size="small" type="date" label={`${schedNoun} date`} InputLabelProps={{ shrink: true }} value={schedDate} onChange={(e) => setSchedDate(e.target.value)} sx={{ width: 175 }} />
          <TextField size="small" label={`${schedNoun} time`} placeholder="6:30 PM" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} sx={{ width: 150 }} />
          <Button size="small" variant="outlined" onClick={saveSched} disabled={savingSched}>{savingSched ? 'Saving…' : 'Save schedule'}</Button>
          <Button size="small" variant="text" startIcon={<MailOutlineIcon />} onClick={() => onEmail(item)}>Email this {noun.toLowerCase()}</Button>
        </Stack>
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}>
                <TableCell>Sponsor</TableCell><TableCell>Role</TableCell><TableCell>Pledge</TableCell><TableCell>Status</TableCell><TableCell align="center">Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {donors.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {d.linked && d.customer_id ? (
                        <Box>
                          <MuiLink component={RouterLink} to={`/devotees/${d.customer_id}`} variant="body2" sx={{ fontWeight: 500 }}>{d.recognize_as}</MuiLink>
                          {!d.custom_recognition && <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>auto name — set recognition</Typography>}
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.recognize_as}</Typography>
                          <Typography variant="caption" color={d.suggested_customer_local_ref ? 'text.secondary' : 'error.main'} sx={{ display: 'block' }}>
                            {d.suggested_customer_local_ref ? `${`${d.s_first || ''} ${d.s_last || ''}`.trim()} · suggested` : 'no donor mapped'}
                          </Typography>
                        </Box>
                      )}
                      {d.linked
                        ? <Tooltip title="Edit recognition wording"><IconButton size="small" onClick={() => onRecognition(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        : <Tooltip title="Map to a devotee"><IconButton size="small" onClick={() => onMap(d)}><LinkIcon fontSize="small" /></IconButton></Tooltip>}
                    </Stack>
                  </TableCell>
                  <TableCell>{roleChip(d.role, noun)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {d.source === 'pledge' ? (
                      <>
                        <MuiLink component="button" type="button" onClick={() => onPledge({ customerId: d.customer_id, itemId: d.item_id, role: d.role, vahana: d.vahana, alankaram: d.vahana, sponsorName: d.recognize_as })} sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}>#{d.pledge.order_number}</MuiLink>
                        {(() => {
                          const pending = d.pledge.status === 'pledge-pending';
                          const bal = Number(d.pledge.balance || 0);
                          const balStr = pending && bal > 0 ? `Balance $${bal.toLocaleString('en-US')}` : '';
                          const dateStr = d.pledge.date ? fmtDate(`${d.pledge.date}T12:00:00`) : '';
                          const text = [balStr, dateStr].filter(Boolean).join(' · ');
                          return text ? <Typography variant="caption" color={balStr ? 'warning.main' : 'text.secondary'} sx={{ display: 'block' }}>{text}</Typography> : null;
                        })()}
                      </>
                    ) : d.source === 'order' ? (
                      <MuiLink component="button" type="button" onClick={() => onPledge({ customerId: d.customer_id, itemId: d.item_id, role: d.role, vahana: d.vahana, alankaram: d.vahana, sponsorName: d.recognize_as })} sx={{ cursor: 'pointer', verticalAlign: 'baseline', textAlign: 'left' }}>
                        {d.possible?.date ? fmtDate(`${d.possible.date}T12:00:00`) : 'donation'}
                        <Typography component="span" variant="caption" color="warning.main" sx={{ display: 'block' }}>donated · no pledge</Typography>
                      </MuiLink>
                    ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {d.source === 'pledge'
                      ? <Chip size="small" variant="outlined" color={d.pledge.status === 'pledge-pending' ? 'warning' : 'success'} label={d.pledge.status === 'pledge-pending' ? 'Pending' : 'Completed'} />
                      : d.source === 'order'
                        ? <Chip size="small" variant="outlined" color="warning" label="No pledge" />
                        : <Chip size="small" variant="outlined" label="No pledge" />}
                  </TableCell>
                  <TableCell align="center">
                    {!d.donor_id ? (
                      <Tooltip title="Set a recognition first to add a note"><span><IconButton size="small" disabled><NoteAddIcon fontSize="small" /></IconButton></span></Tooltip>
                    ) : d.note ? (
                      <Tooltip title={d.note}><IconButton size="small" sx={{ color: 'warning.dark' }} onClick={() => onNote({ donor_id: d.donor_id, title: d.recognize_as, current: d.note || '' })}><StickyNote2Icon fontSize="small" /></IconButton></Tooltip>
                    ) : (
                      <Tooltip title="Add note"><IconButton size="small" sx={{ opacity: 0.4 }} onClick={() => onNote({ donor_id: d.donor_id, title: d.recognize_as, current: '' })}><NoteAddIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}

export default function VahanaRoster({ slug, data, reload }) {
  const { staff } = useAuth();
  const program = data?.program;
  const event = data?.event;
  const items = useMemo(() => data?.items || [], [data]);
  const noun = program?.item_noun || 'Vahana';
  const nounLower = noun.toLowerCase();
  const nounPluralLower = (program?.item_noun_plural || `${noun}s`).toLowerCase();
  const schedNoun = program?.schedule_noun || 'Procession';

  const [donors, setDonors] = useState([]);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [mapFilter, setMapFilter] = useState('all');
  const [pledgeFilter, setPledgeFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [emailCtx, setEmailCtx] = useState(null);
  const [pledgeCtx, setPledgeCtx] = useState(null);
  const [noteCtx, setNoteCtx] = useState(null);
  const [linkCtx, setLinkCtx] = useState(null);
  const [recoCtx, setRecoCtx] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const loadDonors = useCallback(async () => {
    const res = await get(`/api/manage/special-sponsorships/${encodeURIComponent(slug)}/donors`, {}, { cache: 'no-store' });
    setDonors(Array.isArray(res?.donors) ? res.donors : []);
  }, [slug]);
  useEffect(() => { loadDonors().catch((e) => setError(e?.message || 'Failed to load sponsors')); }, [loadDonors]);

  const qlc = q.trim().toLowerCase();
  const filtersActive = qlc.length > 0 || roleFilter !== 'all' || mapFilter !== 'all' || pledgeFilter !== 'all';
  const vahanaGroups = useMemo(() => items.map((item) => {
    const vahMatch = qlc ? String(item.name).toLowerCase().includes(qlc) : false;
    const list = donors.filter((d) => String(d.item_id) === String(item.id)).filter((d) => {
      if (roleFilter !== 'all' && d.role !== roleFilter) return false;
      if (mapFilter === 'mapped' && !d.linked) return false;
      if (mapFilter === 'unmapped' && d.linked) return false;
      if (pledgeFilter === 'pledged' && d.source !== 'pledge') return false;
      if (pledgeFilter === 'possible' && d.source !== 'order') return false;
      if (pledgeFilter === 'manual' && d.source !== 'manual') return false;
      if (pledgeFilter === 'pending' && !(d.pledge && d.pledge.status === 'pledge-pending')) return false;
      if (qlc && !vahMatch) {
        const hay = `${d.recognize_as || ''} ${d.first_name || ''} ${d.last_name || ''}`.toLowerCase();
        if (!hay.includes(qlc)) return false;
      }
      return true;
    });
    return { item, donors: list };
  }).filter((g) => g.donors.length > 0), [items, donors, qlc, roleFilter, mapFilter, pledgeFilter]);

  const toggleExpanded = (id) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const resetFilters = () => { setQ(''); setRoleFilter('all'); setMapFilter('all'); setPledgeFilter('all'); };

  // Clickable summary stats (counts across the whole roster; each applies a filter).
  const stats = useMemo(() => ({
    total: donors.length,
    daata: donors.filter((d) => d.role === 'daata').length,
    ubhaya: donors.filter((d) => d.role === 'ubhaya_daata').length,
    pledged: donors.filter((d) => d.source === 'pledge').length,
    possible: donors.filter((d) => d.source === 'order').length,
    nopledge: donors.filter((d) => d.source !== 'pledge').length,
  }), [donors]);

  // Distinct donors (by linked devotee, else by recognize-as) for the search autocomplete, each with the
  // number of vahanams they sponsor and the Daata / Ubhaya breakup.
  const donorOptions = useMemo(() => {
    const map = new Map();
    donors.forEach((d) => {
      const key = d.customer_id ? `c${d.customer_id}` : `n${(d.recognize_as || '').toLowerCase()}`;
      if (!map.has(key)) map.set(key, { key, label: d.recognize_as || '(sponsor)', name: `${d.first_name || ''} ${d.last_name || ''}`.trim(), vahanas: new Set(), daata: 0, ubhaya: 0 });
      const o = map.get(key);
      o.vahanas.add(d.item_id);
      if (d.role === 'daata') o.daata += 1; else if (d.role === 'ubhaya_daata') o.ubhaya += 1;
    });
    return [...map.values()].map((o) => ({ key: o.key, label: o.label, name: o.name, count: o.vahanas.size, daata: o.daata, ubhaya: o.ubhaya }));
  }, [donors]);

  const linkCustomer = async (donorId, localRef) => {
    try { await patch(`/api/manage/special-sponsorships/donors/${donorId}`, { customer_local_ref: localRef }); setLinkCtx(null); await loadDonors(); await reload(); }
    catch (e) { setError(e?.message || 'Failed to map donor'); }
  };
  const saveNote = async (text) => {
    if (!noteCtx?.donor_id) { setError('Set a recognition for this sponsor before adding a note.'); return; }
    try { await patch(`/api/manage/special-sponsorships/donors/${noteCtx.donor_id}`, { note: text }); await loadDonors(); setNoteCtx(null); }
    catch (e) { setError(e?.message || 'Failed to save note'); }
  };

  // Standard roster print — same flat layout as the saree roster, with vahana details: one row per sponsor,
  // ordered by vahanam, then role (Daata first), then name.
  const printRoster = () => {
    try {
      const rank = (r) => (r === 'daata' ? 0 : r === 'ubhaya_daata' ? 1 : 2);
      const nameKey = (d) => `${(d.first_name || '').trim() || d.recognize_as || ''} ${d.last_name || ''}`.toLowerCase();
      const sorted = [...donors].sort((a, b) => (a.item_sort - b.item_sort) || (rank(a.role) - rank(b.role)) || nameKey(a).localeCompare(nameKey(b)));
      const rowsHtml = sorted.map((d) => {
        const fam = (d.family || []).map((f) => `${escHtml(f.name)}${f.relation ? ` (${escHtml(f.relation)})` : ''}`).join(', ');
        let pledgeCell = '—'; let statusCell = '—';
        if (d.source === 'pledge') {
          const p = d.pledge || {};
          const pending = p.status === 'pledge-pending';
          const bal = Number(p.balance || 0);
          const balStr = pending && bal > 0 ? `Balance $${bal.toLocaleString('en-US')}` : '';
          const dateStr = p.date ? escHtml(fmtDate(`${p.date}T12:00:00`)) : '';
          const pSub = [balStr, dateStr].filter(Boolean).join(' · ');
          pledgeCell = `#${escHtml(p.order_number)}${pSub ? `<div class="sub">${pSub}</div>` : ''}`;
          statusCell = pending ? 'Pending' : 'Completed';
        } else if (d.source === 'order') {
          const dateStr = d.possible?.date ? escHtml(fmtDate(`${d.possible.date}T12:00:00`)) : '';
          pledgeCell = `${dateStr}<div class="sub">donated · no pledge</div>`;
          statusCell = 'No pledge';
        }
        return `<tr><td>${escHtml(d.vahana)}</td><td class="role">${escHtml(roleText(d.role, noun))}</td>`
          + `<td><b>${escHtml(d.recognize_as || '')}</b></td><td>${escHtml(d.spouse || '—')}</td><td>${escHtml(d.gothram || '—')}</td>`
          + `<td>${fam || '—'}</td><td>${pledgeCell}</td><td>${statusCell}</td></tr>`;
      }).join('');
      const evt = event ? `${escHtml(event.title)}${event.event_date ? ` · ${fmtDate(event.event_date)}` : ''}` : '';
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(program?.name || 'Vahana Sponsors')}</title>
        <style>*{box-sizing:border-box}body{font-family:Georgia,'Times New Roman',serif;color:#1c1c1c;margin:0;padding:28px 32px;line-height:1.45}
        .head{text-align:center;border-bottom:2px solid #9a540f;padding-bottom:12px;margin-bottom:18px}.org{font-size:13px;letter-spacing:.08em;text-transform:uppercase;color:#9a540f}
        h1{font-size:22px;margin:4px 0 2px}.sub{font-size:12px;color:#555}
        table{width:100%;border-collapse:collapse;font-size:12.5px}th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#666;border-bottom:1px solid #ddd;padding:5px 7px}
        td{padding:6px 7px;border-bottom:1px solid #eee;vertical-align:top}td.role,th.role{width:110px;color:#444}
        .foot{margin-top:18px;font-size:11px;color:#888;text-align:center}@media print{body{padding:0}@page{margin:14mm;size:landscape}}</style></head><body>
        <div class="head"><div class="org">ICCT Memphis · Sri Venkateswara Temple</div><h1>${escHtml(program?.name || 'Vahana Sponsors')}</h1>${evt ? `<div class="sub">${evt}</div>` : ''}</div>
        <table><thead><tr><th>${escHtml(noun)}</th><th>Role</th><th>Sponsor</th><th>Spouse</th><th>Gothram</th><th>Family members</th><th>Pledge</th><th>Status</th></tr></thead><tbody>${donors.length ? rowsHtml : '<tr><td colspan="8">No sponsors yet.</td></tr>'}</tbody></table>
        <div class="foot">${donors.length} sponsors · generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
        <script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`;
      const e = printHtml(html); if (e) setError(e);
    } catch (e) { setError(e?.message || 'Failed to build roster'); }
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ width: '100%' }}>
        <RosterHeader program={program}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={printRoster}>Print roster</Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add sponsor</Button>
          <Button variant="contained" startIcon={<MailOutlineIcon />} onClick={() => setEmailCtx({ itemId: null, label: `all ${nounPluralLower}` })}>Send recognition email</Button>
        </RosterHeader>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <LinkedEventBanner event={event} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(6,1fr)' }, gap: 1.5, mb: 2 }}>
          {[
            { k: 'Total', v: stats.total, active: !filtersActive, onClick: resetFilters },
            { k: `${noun} Daata`, v: stats.daata, active: roleFilter === 'daata', onClick: () => setRoleFilter((r) => r === 'daata' ? 'all' : 'daata'), color: 'primary.main' },
            { k: 'Ubhaya Daata', v: stats.ubhaya, active: roleFilter === 'ubhaya_daata', onClick: () => setRoleFilter((r) => r === 'ubhaya_daata' ? 'all' : 'ubhaya_daata'), color: 'warning.main' },
            { k: 'Pledged', v: stats.pledged, active: pledgeFilter === 'pledged', onClick: () => setPledgeFilter((p) => p === 'pledged' ? 'all' : 'pledged'), color: 'success.main' },
            { k: 'Possible match', v: stats.possible, active: pledgeFilter === 'possible', onClick: () => setPledgeFilter((p) => p === 'possible' ? 'all' : 'possible'), color: stats.possible > 0 ? 'warning.main' : 'text.primary' },
            { k: 'No pledge', v: stats.nopledge, active: pledgeFilter === 'manual', onClick: () => setPledgeFilter((p) => p === 'manual' ? 'all' : 'manual'), color: 'text.primary' },
          ].map((s) => (
            <Paper key={s.k} variant="outlined" onClick={s.onClick}
              sx={{ p: 1.5, cursor: 'pointer', borderColor: s.active ? 'primary.main' : 'divider', bgcolor: s.active ? 'primary.50' : 'background.paper', '&:hover': { borderColor: 'primary.light' } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: s.color || 'text.primary' }}>{s.v ?? '—'}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.k}</Typography>
            </Paper>
          ))}
        </Box>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
          <Autocomplete freeSolo size="small" sx={{ minWidth: 260, flexGrow: 1, maxWidth: 420 }} options={donorOptions}
            inputValue={q} onInputChange={(e, val) => setQ(val)}
            onChange={(e, val) => { if (val && typeof val !== 'string') setQ(val.label); }}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
            filterOptions={(opts, { inputValue }) => {
              const s = inputValue.trim().toLowerCase();
              if (s.length < 3) return [];
              return opts.filter((o) => `${o.label} ${o.name}`.toLowerCase().includes(s)).slice(0, 8);
            }}
            renderOption={(props, o) => (
              <li {...props} key={o.key}>
                <Box>
                  <Typography variant="body2">{o.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {o.count} {nounLower}{o.count === 1 ? '' : 's'}{o.daata ? ` · ${o.daata} ${noun} Daata` : ''}{o.ubhaya ? ` · ${o.ubhaya} Ubhaya` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder={`Search ${nounLower} or sponsor name`}
                InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            )} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="all">All roles</MenuItem>
              <MenuItem value="daata">{noun} Daata</MenuItem>
              <MenuItem value="ubhaya_daata">Ubhaya Daata</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Mapping</InputLabel>
            <Select label="Mapping" value={mapFilter} onChange={(e) => setMapFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="mapped">Mapped</MenuItem>
              <MenuItem value="unmapped">Needs mapping</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Pledge</InputLabel>
            <Select label="Pledge" value={pledgeFilter} onChange={(e) => setPledgeFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pledged">Pledged</MenuItem>
              <MenuItem value="possible">Possible match</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="manual">No pledge</MenuItem>
            </Select>
          </FormControl>
          {filtersActive
            ? <Button size="small" onClick={resetFilters}>Clear</Button>
            : (<>
                <Button size="small" onClick={() => setExpanded(new Set(vahanaGroups.map((g) => g.item.id)))}>Expand all</Button>
                <Button size="small" onClick={() => setExpanded(new Set())}>Collapse all</Button>
              </>)}
        </Stack>

        {vahanaGroups.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No sponsors match.</Paper>
        ) : vahanaGroups.map((g) => (
          <VahanaSection key={g.item.id} item={g.item} donors={g.donors} noun={noun} schedNoun={schedNoun}
            open={filtersActive || expanded.has(g.item.id)}
            onToggle={() => { if (!filtersActive) toggleExpanded(g.item.id); }}
            onMap={setLinkCtx}
            onRecognition={(d) => setRecoCtx({ item_id: d.item_id, local_ref: d.local_ref, role: d.role, recognize_as: d.custom_recognition ? d.recognize_as : '', vahana: d.vahana })}
            onPledge={setPledgeCtx} onNote={setNoteCtx}
            onEmail={(it) => setEmailCtx({ itemId: it.id, label: it.name })}
            onSchedSaved={reload} />
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Sponsors are grouped by {nounLower}. Expand one to see its sponsors, edit its {schedNoun.toLowerCase()} schedule, or email it. The Pledge column shows the matched {nounLower} donation (click for details), or the devotee's first order date when no {nounLower} donation could be matched. Use the link icon to change the mapped donor.
        </Typography>
      </Box>

      <MapDonorDialog ctx={linkCtx} onClose={() => setLinkCtx(null)} onPick={(localRef) => linkCustomer(linkCtx.donor_id, localRef)} />
      <RecognitionDialog ctx={recoCtx} slug={slug} onClose={() => setRecoCtx(null)} onSaved={async () => { await loadDonors(); await reload(); }} />
      <AddSponsorDialog open={addOpen} items={items} slug={slug} noun={noun} onClose={() => setAddOpen(false)} onAdded={async () => { setAddOpen(false); await loadDonors(); await reload(); }} />
      <EmailDialog open={!!emailCtx} slug={slug} itemId={emailCtx?.itemId} scopeLabel={emailCtx?.label || `all ${nounPluralLower}`} myEmail={staff?.email || ''} onClose={() => setEmailCtx(null)} />
      <PledgeHistoryDialog ctx={pledgeCtx} slug={slug} onClose={() => setPledgeCtx(null)} />
      <NoteDialog ctx={noteCtx} onSave={saveNote} onClose={() => setNoteCtx(null)} />
    </PageLayout>
  );
}
